<template>
  <v-card>
    <v-card-title class="d-flex align-center flex-wrap">
      <span class="me-3 error--text">
        {{ title }}
      </span>
      
      <!-- 
        ============================================
        MONTH SELECTOR DROPDOWN 
        ============================================
        v-select สำหรับเลือกเดือนย้อนหลัง
        
        แสดงเฉพาะเมื่อ:
        - reportType === 'daily' (ไม่แสดงใน monthly)
        - monthOptions.length > 0 (มีตัวเลือกให้เลือก)
        
        Props:
        - v-model="selectedMonth"  → เก็บค่าที่เลือก
        - :items="monthOptions"    → ตัวเลือก จาก props
        - item-text="text"         → แสดง text เช่น "พ.ย. 2568"
        - item-value="value"       → ค่า {year, month}
        - @change="onMonthChange"  → เรียกเมื่อเปลี่ยน
      -->
      <v-select
        v-if="reportType === 'daily' && monthOptions.length > 0"
        v-model="selectedMonth"
        :items="monthOptions"
        item-text="text"
        item-value="value"
        dense
        outlined
        hide-details
        style="max-width: 160px;"
        @change="onMonthChange"
      />
    </v-card-title>
    
    <v-card-text v-if="reportType === 'daily'" class="pt-0">
      ยอดผู้เล่นจะอัพเดตทุก 1:00 AM (ตี 1) ของวันถัดไป
    </v-card-text>
    
    <v-divider />
    
    <v-card-actions class="pa-0">
      <!-- 
        ============================================
        DATA TABLE - เพิ่ม :loading
        ============================================
        เพิ่ม :loading="loading" เพื่อแสดง loading indicator
        ตอนโหลดข้อมูลเดือนใหม่
      -->
      <v-data-table
        mobile-breakpoint="0"
        style="width: 100%"
        :headers="headers"
        :items="reports"
        :loading="loading"
        :no-data-text="$t('not_found')"
        :no-results-text="$t('not_found')"
        hide-default-footer
        disable-pagination
      >
        <template v-slot:[`item.deposit`]="{ item }">
          <span>{{ item.deposit | currency }}</span>
        </template>
        <template v-slot:[`item.withdraw`]="{ item }">
          <span>{{ item.withdraw | currency }}</span>
        </template>
        <template v-slot:[`item.profit`]="{ item }">
          <span>{{ item.profit | currency }}</span>
        </template>
      </v-data-table>
    </v-card-actions>

    <!-- 
      ============================================
      TOTAL PROFIT - เปลี่ยนจาก totalProfit เป็น calculatedTotalProfit
      ============================================
      คำนวณใหม่จาก reports array เมื่อเปลี่ยนเดือน
    -->
    <div v-if="reportType === 'daily'" class="pb-8">
      <div style="border-top: 1px solid #ddd"></div>
      <div class="v-data-table theme--light" style="width: 100%">
        <div class="v-data-table__wrapper">
          <table>
            <tbody>
              <tr style="border-top: 1px solid #ccc">
                <td>
                  <span style="font-weight: bold">กำไรสุทธิ</span>
                </td>
                <td class="col-1">
                  <span style="font-weight: bold">{{ calculatedTotalProfit | currency }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import moment from 'moment-timezone'

export default {
  props: {
    reportType: {
      type: String,
      default: 'daily',
    },
    title: {
      type: String,
      default: '',
    },
    reports: {
      type: Array,
      default: () => [],
    },
    totalProfit: {
      type: Number,
      default: 0,
    },
    currentMonthYear: {
      type: String,
      default: '',
    },
    /**
     * monthOptions - ตัวเลือกสำหรับ dropdown
     * Format: [{ text: "พ.ย. 2568", value: { year: 2025, month: 11 } }, ...]
     */
    monthOptions: {
      type: Array,
      default: () => [],
    },
    /**
     * defaultMonth - ค่าเริ่มต้นสำหรับ dropdown
     * Format: { year: 2025, month: 11 }
     */
    defaultMonth: {
      type: Object,
      default: () => ({}),
    },
    /**
     * loading - แสดง loading indicator ใน data table
     */
    loading: {
      type: Boolean,
      default: false,
    },
  },
  
  data() {
    return {
      /**
       * selectedMonth - เก็บค่าเดือนที่เลือกจาก dropdown
       * ใช้กับ v-model ของ v-select
       */
      selectedMonth: null,
      
      dailyHeaders: [
        {
          text: this.$t('dashboard.block7_date'),
          value: 'day',
          sortable: false,
        },
        {
          text: 'ยอดผู้เล่น',
          value: 'active_users',
          align: 'end',
          sortable: false,
        },
        {
          text: this.$t('dashboard.block7_deposit'),
          value: 'deposit',
          align: 'end',
          sortable: false,
        },
        {
          text: this.$t('dashboard.block7_withdraw'),
          value: 'withdraw',
          align: 'end',
          sortable: false,
        },
        {
          text: this.$t('dashboard.block7_net_profit'),
          value: 'profit',
          align: 'end',
          sortable: false,
        },
      ],
      monthlyHeaders: [
        {
          text: this.$t('dashboard.block8_month'),
          value: 'month',
          sortable: false,
        },
        {
          text: this.$t('dashboard.block8_deposit'),
          value: 'deposit',
          align: 'end',
          sortable: false,
        },
        {
          text: this.$t('dashboard.block8_withdraw'),
          value: 'withdraw',
          align: 'end',
          sortable: false,
        },
        {
          text: this.$t('dashboard.block8_net_profit'),
          value: 'profit',
          align: 'end',
          sortable: false,
        },
      ],
    }
  },
  
  computed: {
    headers() {
      return this.reportType === 'daily' ? this.dailyHeaders : this.monthlyHeaders
    },
    /**
     * calculatedTotalProfit - คำนวณกำไรรวมจาก reports array
     * 
     * เหตุผลที่ใช้ computed แทน props:
     * เมื่อเปลี่ยนเดือน reports จะเปลี่ยน แต่ totalProfit จาก props
     * อาจไม่อัพเดตทัน ดังนั้นคำนวณเองจาก reports จะแม่นกว่า
     */
    calculatedTotalProfit() {
      if (!this.reports || this.reports.length === 0) return 0
      return this.reports.reduce((sum, item) => sum + parseFloat(item.profit || 0), 0)
    },
  },
  
  watch: {
    /**
     * เมื่อ defaultMonth เปลี่ยน (จาก parent)
     * ให้ set selectedMonth ตาม
     * 
     * immediate: true → ทำงานทันทีตอน component mount
     */
    defaultMonth: {
      immediate: true,
      handler(val) {
        if (val && val.year && val.month) {
          this.selectedMonth = val
        }
      },
    },
  },
  
  methods: {
    /**
     * onMonthChange - เรียกเมื่อ user เปลี่ยนเดือนใน dropdown
     * 
     * @param {Object} value - ค่าที่เลือก { year, month }
     * 
     * emit 'month-change' event ไปให้ parent (Dashboard.vue)
     * เพื่อให้ parent ไปเรียก API ดึงข้อมูลเดือนใหม่
     */
    onMonthChange(value) {
      this.$emit('month-change', value)
    },
  },
}
</script>